import {
  Button,
  Dialog,
  Paper,
  Grid,
  IconButton,
  Stack,
  styled,
  TablePagination,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  useMediaQuery,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Chip,
  Divider,
  Switch,
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import TableViewIcon from '@mui/icons-material/TableView';
import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Container } from 'reactstrap';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import axios from '../../utils/axios';
import UIContext from '../../context/UIContext';

const CustomerCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
}));

const CustomerInfo = styled('div')({
  padding: '12px',
  flexGrow: 1,
});

const CustomerInitialValue = {
  CategoryId: null,
  FirstName: '',
  LastName: '',
  Email: '',
  Mobile: '',
  Password: '',
  Address1: '',
  Address2: '',
  City: '',
  State: '',
  Pincode: '',
  Active: 'Y',
};

const filterInitialValue = {
  searchTerm: '',
  Active: true,
};

const Customer = () => {
  const [filterData, setFilterData] = useState(filterInitialValue);
  const [CustomerTableData, setCustomerTableData] = useState([]);
  const [Customer, setCustomer] = useState([]);
  const [UserId, setUserId] = useState(null);
  const [initialValue, setInitialValue] = useState(CustomerInitialValue);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'

  // snackbar context
  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);

  // api fetch data
  useEffect(() => {
    const UserId = sessionStorage.getItem('UserId');
    setUserId(UserId);
    GetAllCustomer();
  }, []);

  const CustomerValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        FirstName: Yup.string()
          .trim()
          .min(2, 'First Name must be at least 2 characters')
          .max(100, 'First Name cannot exceed 100 characters')
          .required('First Name is required'),

        LastName: Yup.string()
          .trim()
          .min(1, 'Last Name must be at least 1 character')
          .max(100, 'Last Name cannot exceed 100 characters'),
        Email: Yup.string()
          .trim()
          .email('Invalid email address')
          .max(100, 'Email cannot exceed 100 characters')
          .required('Email is required')
          .notOneOf(
            Customer.filter((item) => item.CustomerId !== initialValue.CustomerId).map((item) => item.Email),
            'Email already exists'
          ),

        Mobile: Yup.string()
          .trim()
          .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits')
          .required('Mobile number is required')
          .notOneOf(
            Customer.filter((item) => item.CustomerId !== initialValue.CustomerId).map((item) => item.Mobile),
            'Mobile number already exists'
          ),

        Password: Yup.string()
          .trim()
          .min(6, 'Password must be at least 6 characters')
          .max(100, 'Password cannot exceed 100 characters')
          .required('Password is required'),

        Address1: Yup.string()
          .trim()
          .max(100, 'Address1 cannot exceed 100 characters')
          .required('Address1 is required'),

        Address2: Yup.string().trim().max(100, 'Address2 cannot exceed 100 characters').nullable(),

        City: Yup.string().trim().max(50, 'City cannot exceed 50 characters').required('City is required'),

        State: Yup.string().trim().max(50, 'State cannot exceed 50 characters').required('State is required'),

        Pincode: Yup.string()
          .trim()
          .matches(/^[0-9]{6}$/, 'Pincode must be exactly 6 digits')
          .required('Pincode is required'),

        Active: Yup.string().oneOf(['Y', 'N'], 'Please select Yes or No').required('Active status is required'),
      }),
    [Customer, initialValue]
  );

  async function GetAllCustomer() {
    try {
      showLoading();
      const response = await axios.get('Master/Customer/GetAllCustomer');
      setCustomer(response.data);
      setCustomerTableData(response.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  }

  async function InsertCustomer(values) {
    try {
      showLoading();
      await axios.post('Master/Customer/InsertCustomer', { ...values, UserId });
      handleSubmitSuccess();
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  }

  const UpdateCustomer = async (values) => {
    try {
      showLoading();
      await axios.post('Master/Customer/UpdateCustomer', { ...values, UserId });
      handleSubmitSuccess();
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  };

  const handleSubmitSuccess = () => {
    GetAllCustomer();
    handleCloseDialog();
    openSnackBar(`Customer ${edit ? 'Updated' : 'Added'} Successfully!`, edit ? 'warning' : 'success');
  };

  const DeleteCustomer = async () => {
    try {
      showLoading();
      await axios.delete(`Master/Customer/DeleteCustomer/${deleteId}`);
      GetAllCustomer();
      openSnackBar('Customer Deleted Successfully!', 'warning');
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    handleCloseDelete();
    setDeleteId(null);
    hideLoading();
  };

  const handleEditClick = (values) => {
    setInitialValue(values);
    setEdit(true);
    setOpen(true);
  };

  const handleFilterDataChange = (name, value) => {
    setFilterData({ ...filterData, [name]: value });
  };

  useEffect(() => {
    const newTableData = Customer.filter(
      (Customer) =>
        `${Customer.FirstName} ${Customer.LastName} ${Customer.Email} ${Customer.Mobile}`
          .toLowerCase()
          .includes(filterData.searchTerm?.toLowerCase()) && Customer.Active === (filterData.Active ? 'Y' : 'N')
    );
    setPage(0);
    setCustomerTableData(newTableData);
  }, [Customer, filterData]);

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDeleteId(null);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEdit(false);
    setInitialValue(CustomerInitialValue);
  };

  // pagination
  const { emptyRows, totalRows, currentPage } = useMemo(() => {
    const totalRows = CustomerTableData.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    const currentPage = Math.max(0, Math.min(page, totalPages - 1));
    const emptyRows = rowsPerPage - Math.min(rowsPerPage, totalRows - page * rowsPerPage);

    return { emptyRows, totalRows, currentPage };
  }, [CustomerTableData, page, rowsPerPage]);

  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'table' : 'grid');
  };

  return (
    <>
      <Container maxWidth={'xl'} sx={{ pt: 2 }} elevation={3}>
        {/* Header Section */}
        <Paper elevation={3} sx={{ p: 1, mb: 3, backgroundColor: '#2A4759', color: 'white' }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h6" color="white" className="font_style1" textAlign={'center'}>
                Customer
              </Typography>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  color="success"
                  onClick={toggleViewMode}
                  startIcon={viewMode === 'grid' ? <TableViewIcon /> : <GridViewIcon />}
                  sx={{ boxShadow: 3 }}
                  size="small"
                >
                  {viewMode === 'grid' ? 'Table View' : 'Grid View'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={() => setOpen(true)}
                  startIcon={<PlaylistAddIcon />}
                  sx={{ boxShadow: 3 }}
                >
                  Add Customer
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Filter Section */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                color="secondary"
                label="Search Customer"
                size="small"
                onChange={(e) => handleFilterDataChange('searchTerm', e.target.value)}
                value={filterData.searchTerm}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Show Active:
                </Typography>
                <Switch
                  size="small"
                  checked={filterData.Active}
                  onChange={(e) => handleFilterDataChange('Active', e.target.checked)}
                  color="success"
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {viewMode === 'grid' ? (
          /* Customer Grid View */
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {(rowsPerPage > 0
              ? CustomerTableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : CustomerTableData
            ).map((Customer) => (
              <Grid item xs={12} sm={6} md={4} key={Customer.Customer_Details_Id}>
                <CustomerCard elevation={4}>
                  <CustomerInfo>
                    <Typography variant="h6" gutterBottom>
                      {`${Customer.FirstName} ${Customer.LastName}`}
                    </Typography>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Chip
                        label={Customer.Email || 'No Email'}
                        color="primary"
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 'bold' }}
                      />
                      <Chip
                        label={Customer.Active === 'Y' ? 'Active' : 'Inactive'}
                        color={Customer.Active === 'Y' ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                        icon={Customer.Active === 'Y' ? <CheckCircleOutlineIcon /> : <HighlightOffIcon />}
                      />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Mobile: {Customer.Mobile || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        City: {Customer.City || 'N/A'}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" color="primary">
                        {Customer.Address1
                          ? `${Customer.Address1}, ${Customer.City}, ${Customer.State} - ${Customer.Pincode}`
                          : 'Address not available'}
                      </Typography>

                      <Box>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(Customer);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(Customer.CustomerId);
                            setOpenDelete(true);
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CustomerInfo>
                </CustomerCard>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* Customer Table View */
          <TableContainer component={Paper} elevation={3} sx={{ mb: 3, maxHeight: '400px' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? CustomerTableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : CustomerTableData
                ).map((Customer) => (
                  <TableRow key={Customer.CustomerId} hover>
                    <TableCell>{`${Customer.FirstName} ${Customer.LastName}`}</TableCell>
                    <TableCell>{Customer.Email}</TableCell>
                    <TableCell>{Customer.Email || 'N/A'}</TableCell>
                    <TableCell>
                      {Customer.Address1} , {Customer.City} , {Customer.State} - {Customer.Pincode}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={Customer.Active === 'Y' ? 'Active' : 'Inactive'}
                        color={Customer.Active === 'Y' ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                        icon={
                          Customer.Active === 'Y' ? (
                            <CheckCircleOutlineIcon fontSize="small" />
                          ) : (
                            <HighlightOffIcon fontSize="small" />
                          )
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary" onClick={() => handleEditClick(Customer)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeleteId(Customer.Customer_Details_Id);
                          setOpenDelete(true);
                        }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={8} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <Box display="flex" justifyContent="center" mt={3}>
          <TablePagination
            rowsPerPageOptions={[12, 24, 36, { label: 'All', value: -1 }]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Box>

        {/* Add/Edit Customer Dialog */}
        <Dialog maxWidth={'md'} open={open} onClose={handleCloseDialog} fullWidth>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {edit ? 'Edit Customer' : 'Add New Customer'}
              </Typography>
              <IconButton onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Formik
              validationSchema={CustomerValidationSchema}
              initialValues={initialValue}
              onSubmit={async (values, { resetForm }) => {
                if (edit) await UpdateCustomer(values);
                else await InsertCustomer(values);
                resetForm();
              }}
              enableReinitialize
            >
              {({ submitForm, values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="First Name"
                        name="FirstName"
                        onBlur={handleBlur}
                        required
                        error={touched.FirstName && !!errors.FirstName}
                        helperText={touched.FirstName && errors.FirstName}
                        value={values.FirstName}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Last Name"
                        name="LastName"
                        onBlur={handleBlur}
                        error={touched.LastName && !!errors.LastName}
                        helperText={touched.LastName && errors.LastName}
                        value={values.LastName}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        name="Email"
                        onBlur={handleBlur}
                        error={touched.Email && !!errors.Email}
                        helperText={touched.Email && errors.Email}
                        value={values.Email}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Mobile"
                        name="Mobile"
                        onBlur={handleBlur}
                        error={touched.Mobile && !!errors.Mobile}
                        helperText={touched.Mobile && errors.Mobile}
                        value={values.Mobile}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Password"
                        name="Password"
                        onBlur={handleBlur}
                        error={touched.Password && !!errors.Password}
                        helperText={touched.Password && errors.Password}
                        value={values.Password}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Address1"
                        name="Address1"
                        multiline
                        rows={2}
                        onBlur={handleBlur}
                        error={touched.Address1 && !!errors.Address1}
                        helperText={touched.Address1 && errors.Address1}
                        value={values.Address1}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Address2"
                        name="Address2"
                        multiline
                        rows={2}
                        onBlur={handleBlur}
                        error={touched.Address2 && !!errors.Address2}
                        helperText={touched.Address2 && errors.Address2}
                        value={values.Address2}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="City"
                        name="City"
                        onBlur={handleBlur}
                        error={touched.City && !!errors.City}
                        helperText={touched.City && errors.City}
                        value={values.City}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="State"
                        name="State"
                        onBlur={handleBlur}
                        error={touched.State && !!errors.State}
                        helperText={touched.State && errors.State}
                        value={values.State}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Pincode"
                        name="Pincode"
                        onBlur={handleBlur}
                        error={touched.Pincode && !!errors.Pincode}
                        helperText={touched.Pincode && errors.Pincode}
                        value={values.Pincode}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend">Active</FormLabel>
                        <RadioGroup row value={values.Active} name="Active" onChange={handleChange}>
                          <FormControlLabel value="Y" control={<Radio color="primary" size="small" />} label="Yes" />
                          <FormControlLabel value="N" control={<Radio color="error" size="small" />} label="No" />
                        </RadioGroup>
                        {errors.Active && touched.Active && (
                          <Typography variant="caption" color="error">
                            {errors.Active}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleCloseDialog}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color={edit ? 'warning' : 'primary'}
                          onClick={submitForm}
                          disabled={isSubmitting}
                        >
                          {edit ? 'Update Customer' : 'Add Customer'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Paper>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDelete} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
          <DialogTitle>
            <Typography variant="h6" fontWeight="bold">
              Confirm Delete
            </Typography>
          </DialogTitle>
          <Box p={3}>
            <Typography>Are you sure you want to delete this Customer? This action cannot be undone.</Typography>
          </Box>
          <DialogActions sx={{ p: 2 }}>
            <Button variant="outlined" onClick={handleCloseDelete}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={DeleteCustomer} autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default Customer;
